import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { Flex, Text } from 'core/components';
import { Field, Form, Switch } from 'core/form';
import { dateRangeDisplay, DEFAULT_DATETIME_FORMAT } from 'core/util/dateUtils';
import VizWrapper from '../utils/VizWrapper';
import Graph from './graph';
import { buildGraphStructure } from './graphBuilder';
import { applyColorCoding } from './graphColorCoding';

const fields = {
  hideASName: {
    defaultValue: false
  }
};

/**
 * Derive column-vertical adjacency from a built graph structure: within each column, connect
 * consecutive ASN nodes top-to-bottom (skipping virtual spacer nodes so the two ASN nodes flanking
 * a virtual are treated as adjacent). Feeds the store's Tier-3 (backup) color constraint.
 */
const getVerticalAdjacency = (graphData) => {
  const { columns = {}, nodes = {} } = graphData;
  const edges = [];

  Object.values(columns).forEach((ids) => {
    const asnIds = ids.filter((id) => nodes[id]?.type === 'asn');
    for (let i = 0; i < asnIds.length - 1; i += 1) {
      edges.push([asnIds[i], asnIds[i + 1]]);
    }
  });

  return edges;
};

@inject('$bgp', '$colors')
@Form({ fields, options: { name: 'Bgp Test Results AS Path Graph Options' } })
@observer
class BgpPathsGraphWrapper extends Component {
  // Vertical adjacency derived during the latest render; flushed to the store once after commit.
  pendingVerticalAdjacency = null;

  componentDidMount() {
    this.flushVerticalAdjacency();
  }

  componentDidUpdate() {
    this.flushVerticalAdjacency();
  }

  // Report column-vertical adjacency to the store outside of render, then clear it so a later
  // render (e.g. after a store reset) can't re-apply stale edges. The store keeps only the layout
  // with the most vertical edges, so repeated/smaller calls are no-ops and this converges.
  flushVerticalAdjacency = () => {
    const { $bgp } = this.props;
    if (this.pendingVerticalAdjacency) {
      $bgp.setPathGraphVerticalAdjacency(this.pendingVerticalAdjacency);
      this.pendingVerticalAdjacency = null;
    }
  };

  renderGraphForm() {
    return (
      <Flex alignItems="center" gap={1}>
        <Field name="hideASName" showLabel={false} large mb={0}>
          <Switch switchLabel="Hide AS Names" large />
        </Field>
      </Flex>
    );
  }

  render() {
    const { $bgp, $colors, form, setPrefix, prefixes, selectedTimeMs, hoveredAsn, model } = this.props;
    const loading = $bgp.asPathGraphTimeseriesLoading;
    const timeseriesData = $bgp.asPathGraphTimeseriesData;

    let results = {};
    let hasResults = false;
    let snappedTimeDisplay = null;
    let selectedGraph = null;

    if (timeseriesData && selectedTimeMs) {
      const { graphs = [], routes, step_seconds, lookups } = timeseriesData;

      if (step_seconds && graphs.length > 0) {
        // Find the graph for the selected time
        const snappedTimeSec = Math.floor(selectedTimeMs / 1000 / step_seconds) * step_seconds;
        snappedTimeDisplay = dateRangeDisplay({
          starting_time: snappedTimeSec * 1000,
          ending_time: null,
          format: DEFAULT_DATETIME_FORMAT
        });

        // Find the graph with the closest timestamp <= snappedTimeSec
        // Graphs are assumed to be ordered by time
        for (let i = graphs.length - 1; i >= 0; i--) {
          const graphTimeSec = parseInt(graphs[i].timestamp?.seconds, 10);
          if (graphTimeSec <= snappedTimeSec) {
            selectedGraph = graphs[i];
            break;
          }
        }
      }

      if (selectedGraph) {
        // Build graph structure from BGP data
        const graphData = buildGraphStructure({
          selectedGraph,
          routes,
          prefixes
        });

        // Apply color coding based on BGP test configuration
        const config = model.get('config.bgp');
        results = applyColorCoding({
          config,
          graphData,
          lookups,
          chartColors: [...$colors.qualitativeColors].reverse(),
          asnColorMap: $bgp.asnColorMap
        });

        // Feed column-vertical adjacency to the store as a backup color constraint (Tier 3).
        this.pendingVerticalAdjacency = getVerticalAdjacency(graphData);

        hasResults = graphData.visibleASNs.length > 0;
      }
    }

    const dataLoaded = timeseriesData !== null;
    const hasData = dataLoaded && timeseriesData.graphs?.length > 0;

    let noResultsDescription;
    if (!dataLoaded || !selectedTimeMs) {
      noResultsDescription = 'Hover over the reachability timeline above to visualize AS paths at a point in time';
    } else if (!hasData) {
      noResultsDescription = 'No AS paths were observed during this time range';
    } else if (selectedGraph) {
      noResultsDescription = 'No AS paths found for the selected prefixes at this time';
    } else {
      noResultsDescription = 'No AS paths were observed at this time';
    }

    return (
      <VizWrapper
        key="bgp-paths-graph"
        loading={loading}
        renderContent={hasResults}
        heading="AS Path Visualization"
        subtitle="Network graph showing all key AS paths associated with the monitored prefixes. Hover over any AS node or link to see more information"
        noResultsDescription={noResultsDescription}
        menu={this.renderGraphForm()}
      >
        <Graph
          {...results}
          setPrefix={setPrefix}
          hideASName={form.getValue('hideASName')}
          prefixes={prefixes}
          asn={hoveredAsn}
        />
        {snappedTimeDisplay && (
          <Flex flexDirection="column" alignItems="center" mt={2}>
            <Text small muted>
              {snappedTimeDisplay}
            </Text>
          </Flex>
        )}
      </VizWrapper>
    );
  }
}

export default BgpPathsGraphWrapper;
